import { FormEvent, useEffect, useMemo, useState, type ReactNode } from "react";
import Swal from "sweetalert2";
import {
  getSiteSettings,
  saveSiteSettings,
  SITE_SETTINGS_DEFAULTS,
  type SiteSettingsPayload,
} from "../api/adminSiteSettingsApi";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import InfoMarkerHint from "../Components/ui/InfoMarkerHint";

type ToggleKey =
  | "mailEventEmailVerification"
  | "mailEventPasswordReset"
  | "mailEventSecurityAlert"
  | "mailEventPaymentSuccess"
  | "mailEventGiftPackage"
  | "mailEventGiftZToken"
  | "mailEventSystemNotification";

const MAIL_EVENT_FIELDS: Array<{ key: ToggleKey; label: string; description: string }> = [
  {
    key: "mailEventEmailVerification",
    label: "E-posta dogrulama",
    description: "Kayit ve verify akisinda email gonderimi.",
  },
  {
    key: "mailEventPasswordReset",
    label: "Sifre sifirlama",
    description: "Sifremi unuttum ve reset linki mailleri.",
  },
  {
    key: "mailEventSecurityAlert",
    label: "Guvenlik uyarisi",
    description: "Guvenlik odakli uyari mailleri.",
  },
  {
    key: "mailEventPaymentSuccess",
    label: "Odeme basarisi",
    description: "Odeme/alisveris basarili oldugunda gonderim.",
  },
  {
    key: "mailEventGiftPackage",
    label: "Hediye paket",
    description: "Hediye paket tanimlandiginda mail gonderimi.",
  },
  {
    key: "mailEventGiftZToken",
    label: "Hediye Z-Token",
    description: "Hediye token tanimlandiginda mail gonderimi.",
  },
  {
    key: "mailEventSystemNotification",
    label: "Sistem bildirimi",
    description: "Sistem kaynakli kritik bildirim mailleri.",
  },
];

const SOCIAL_FIELDS: Array<{ key: keyof SiteSettingsPayload; label: string; placeholder: string }> = [
  { key: "discordUrl", label: "Discord", placeholder: "https://discord.gg/..." },
  { key: "instagramUrl", label: "Instagram", placeholder: "https://instagram.com/..." },
  { key: "tiktokUrl", label: "TikTok", placeholder: "https://tiktok.com/@..." },
  { key: "youtubeUrl", label: "YouTube", placeholder: "https://youtube.com/@..." },
  { key: "twitterUrl", label: "X / Twitter", placeholder: "https://x.com/..." },
  { key: "facebookUrl", label: "Facebook", placeholder: "https://facebook.com/..." },
];

function cloneDefaults(): SiteSettingsPayload {
  return { ...SITE_SETTINGS_DEFAULTS };
}

export default function AdminSiteSettings() {
  const { loading: permissionLoading, allowed } = usePermissionCheck("SiteSettings.Manage");
  const [form, setForm] = useState<SiteSettingsPayload>(cloneDefaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadedAt, setLoadedAt] = useState<string | null>(null);

  const hasValue = useMemo(
    () =>
      Object.entries(form).some(([key, value]) => {
        if (key === "rowVersionToken") {
          return false;
        }
        if (typeof value === "boolean") {
          return value !== true;
        }
        return value.trim().length > 0;
      }),
    [form],
  );

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!allowed) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const payload = await getSiteSettings();
        if (!active) {
          return;
        }
        setForm(payload);
        setLoadedAt(new Date().toLocaleTimeString("tr-TR"));
      } catch {
        if (!active) {
          return;
        }
        setForm(cloneDefaults());
        await Swal.fire("Hata", "Site ayarlari yuklenemedi.", "error");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [allowed]);

  const setField = (key: keyof SiteSettingsPayload, value: string | boolean) => {
    setForm((prev) => ({
      ...prev,
      [key]: typeof value === "string" ? value : Boolean(value),
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    try {
      const result = await saveSiteSettings(form);
      if (!result.success) {
        await Swal.fire("Hata", result.message ?? "Ayarlar kaydedilemedi.", "error");
        return;
      }
      if (typeof result.rowVersionToken === "string" && result.rowVersionToken.length > 0) {
        setForm((prev) => ({ ...prev, rowVersionToken: result.rowVersionToken! }));
      }
      setLoadedAt(new Date().toLocaleTimeString("tr-TR"));
      await Swal.fire("Basarili", "Site ayarlari kaydedildi.", "success");
    } catch {
      await Swal.fire("Hata", "Ayarlar kaydedilirken bir hata olustu.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReload = async () => {
    if (!allowed) {
      return;
    }

    setLoading(true);
    try {
      const payload = await getSiteSettings();
      setForm(payload);
      setLoadedAt(new Date().toLocaleTimeString("tr-TR"));
    } catch {
      await Swal.fire("Hata", "Ayarlar yenilenemedi.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleResetDefaults = async () => {
    const confirm = await Swal.fire({
      title: "Varsayilanlara donulsun mu?",
      text: "Bu islem formdaki mevcut degisiklikleri siler.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Evet, sifirla",
      cancelButtonText: "Vazgec",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setForm(cloneDefaults());
  };

  if (permissionLoading || loading) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
        Site ayarlari yukleniyor...
      </section>
    );
  }

  if (!allowed) {
    return (
      <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        Site ayarlarini gormek veya duzenlemek icin gerekli yetkin bulunmuyor.
      </section>
    );
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <header className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-900">Site Ayarlari</h2>
              <InfoMarkerHint markerKey="alerts.system" />
            </div>
            <p className="text-sm text-slate-600">
              Marka, SEO, sosyal linkler ve sistem mailleri tek merkezden yonetilir.
            </p>
            {loadedAt && <p className="mt-1 text-xs text-slate-500">Son yukleme: {loadedAt}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleReload}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Yenile
            </button>
            <button
              type="button"
              onClick={handleResetDefaults}
              className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100"
              disabled={!hasValue}
            >
              Varsayilana don
            </button>
            <button
              type="submit"
              className="rounded-lg border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-60"
              disabled={saving}
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </div>
      </header>

      <section className="grid gap-4 lg:grid-cols-2">
        <Panel title="Marka Kimligi">
          <Input
            label="Uygulama adi"
            value={form.appName}
            onChange={(value) => setField("appName", value)}
            placeholder="Zenitoon"
          />
          <Input
            label="Header logo adresi"
            value={form.headerLogoUrl}
            onChange={(value) => setField("headerLogoUrl", value)}
            placeholder="/uploads/seed/placeholders/header-logo.svg"
          />
          <Input
            label="Footer logo adresi"
            value={form.footerLogoUrl}
            onChange={(value) => setField("footerLogoUrl", value)}
            placeholder="/uploads/seed/placeholders/footer-logo.svg"
          />
          <Input
            label="Param logo adresi"
            value={form.paramLogoUrl}
            onChange={(value) => setField("paramLogoUrl", value)}
            placeholder="/uploads/seed/placeholders/param-logo.svg"
          />
          <Input
            label="Favicon adresi"
            value={form.faviconUrl}
            onChange={(value) => setField("faviconUrl", value)}
            placeholder="/uploads/seed/placeholders/favicon.svg"
          />
          <TextArea
            label="Footer aciklamasi"
            value={form.footerDescription}
            onChange={(value) => setField("footerDescription", value)}
            rows={4}
          />
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-600">
              Varsayilan kullanici isim rengi
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(form.defaultUserNameColor) ? form.defaultUserNameColor : "#FFFFFF"}
                onChange={(e) => setField("defaultUserNameColor", e.target.value.toUpperCase())}
                className="h-10 w-14 cursor-pointer rounded border border-slate-300 bg-white p-1"
                title="Renk sec"
              />
              <input
                type="text"
                value={form.defaultUserNameColor}
                onChange={(e) => setField("defaultUserNameColor", e.target.value)}
                placeholder="#FFFFFF"
                className="w-32 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono uppercase focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-300"
              />
              <span
                className="rounded-md px-3 py-1.5 text-sm font-semibold"
                style={{ color: /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(form.defaultUserNameColor) ? form.defaultUserNameColor : "#FFFFFF", background: "#0f172a" }}
              >
                Ornek Isim
              </span>
            </div>
            <p className="text-[11px] text-slate-400">
              Kendi rengini secmemis tum uyelerin profil ve yorum isim rengi bu deger olur.
            </p>
          </div>
        </Panel>

        <Panel title="Iletisim, SEO ve Reklam">
          <Input
            label="Destek e-postasi"
            value={form.supportEmail}
            onChange={(value) => setField("supportEmail", value)}
            placeholder="business@zenitoon.com"
          />
          <Input
            label="Is e-postasi"
            value={form.businessEmail}
            onChange={(value) => setField("businessEmail", value)}
            placeholder="business@zenitoon.com"
          />
          <Input
            label="Google site dogrulamasi"
            value={form.googleSiteVerification}
            onChange={(value) => setField("googleSiteVerification", value)}
            placeholder="google-site-verification=..."
          />
          <Input
            label="AdSense istemci ID"
            value={form.adsenseClientId}
            onChange={(value) => setField("adsenseClientId", value)}
            placeholder="ca-pub-xxxxxxxxxxxxxxxx"
          />
          <Input
            label="AdSense paket slotu"
            value={form.adsensePackagesSlot}
            onChange={(value) => setField("adsensePackagesSlot", value)}
            placeholder="1234567890"
          />
          <Input
            label="AdSense uye paket slotu"
            value={form.adsenseUserPackagesSlot}
            onChange={(value) => setField("adsenseUserPackagesSlot", value)}
            placeholder="0987654321"
          />
        </Panel>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <Panel title="Sosyal linkler">
          {SOCIAL_FIELDS.map((item) => (
            <Input
              key={item.key}
              label={item.label}
              value={String(form[item.key] ?? "")}
              onChange={(value) => setField(item.key, value)}
              placeholder={item.placeholder}
            />
          ))}
        </Panel>

        <Panel title="Mail gonderim anahtarlari">
          <div className="space-y-3">
            {MAIL_EVENT_FIELDS.map((field) => (
              <label
                key={field.key}
                className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3"
              >
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={Boolean(form[field.key])}
                  onChange={(event) => setField(field.key, event.target.checked)}
                />
                <span>
                  <span className="block text-sm font-semibold text-slate-800">{field.label}</span>
                  <span className="block text-xs text-slate-600">{field.description}</span>
                </span>
              </label>
            ))}
          </div>
        </Panel>
      </section>
    </form>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">{title}</h3>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}

function Input({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block text-xs font-medium text-slate-600">
      {label}
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
      />
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  rows,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
}) {
  return (
    <label className="block text-xs font-medium text-slate-600">
      {label}
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={rows ?? 3}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
      />
    </label>
  );
}

